import React, { useEffect, useState } from 'react';
import adminApi from '../../clients/AdminClient';
import { type BookingLink } from '../../models/Membership';
import { type AddBookingLinkRequest } from '../../models/admin/AddBookingLinkRequest';

const NewBookingLink: React.FC = () => {
  const [form, setForm] = useState<AddBookingLinkRequest>({ url: '', title: '', description: '', membershipId: 0, locationId: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [links, setLinks] = useState<BookingLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [memberships, setMemberships] = useState<Array<{ id: number; name: string }>>([]);
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);

  const loadLinks = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await adminApi.GetAllBookingLinks();
      setLinks(data);
    } catch (e) {
      setError('Failed to load booking links');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
    (async () => {
      try {
        const [m, l] = await Promise.all([
          adminApi.GetMemberships(),
          adminApi.GetLocations()
        ]);
        setMemberships(m);
        setLocations(l);
        setForm(prev => ({
          ...prev,
          membershipId: m && m.length > 0 ? m[0].id : 0,
          locationId: l && l.length > 0 ? l[0].id : 0
        }));
      } catch (e) {
        setError('Failed to load dropdown data');
      }
    })();
  }, []);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const created = await adminApi.AddBookingLink(form);
      setSuccess('Booking link created');
      setForm({
        url: '',
        title: '',
        description: '',
        membershipId: memberships && memberships.length > 0 ? memberships[0].id : 0,
        locationId: locations && locations.length > 0 ? locations[0].id : 0
      });
      setLinks([created, ...links]);
    } catch (e) {
      setError('Failed to create booking link');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container py-4">
      <h1 className="heading mb-4">Booking Links</h1>

      <div className="row g-4">
        <div className="col-12">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title heading">Create New Booking Link</h5>

              {error && <div className="alert alert-danger" role="alert">{error}</div>}
              {success && <div className="alert alert-success" role="alert">{success}</div>}

              <form onSubmit={onSubmit}>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">Title</label>
                  <input
                    id="title"
                    name="title"
                    className="form-control"
                    value={form.title}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="membershipId" className="form-label">Membership</label>
                  <select
                    id="membershipId"
                    name="membershipId"
                    className="form-select bg-dark text-white border-secondary"
                    value={form.membershipId ?? 0}
                    onChange={e => setForm(prev => ({ ...prev, membershipId: Number(e.target.value) }))}
                    required
                  >
                    {memberships.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="locationId" className="form-label">Location</label>
                  <select
                    id="locationId"
                    name="locationId"
                    className="form-select bg-dark text-white border-secondary"
                    value={form.locationId ?? 0}
                    onChange={e => setForm(prev => ({ ...prev, locationId: Number(e.target.value) }))}
                    required
                  >
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="url" className="form-label">Url</label>
                  <textarea
                    id="url"
                    name="url"
                    className="form-control"
                    rows={5}
                    value={form.url}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control"
                    rows={5}
                    value={form.description}
                    onChange={onChange}
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create'}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title heading">All Booking Links</h5>
              {isLoading && (
                <div className="text-center py-4">
                  <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
                </div>
              )}
              {!isLoading && links.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-paragraphs mb-0">No booking links found.</p>
                </div>
              )}
              {!isLoading && links.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-striped table-hover table-dark align-middle">
                    <thead>
                      <tr>
                        <th className="text-start">Title</th>
                        <th className="text-start">Description</th>
                        <th className="text-start">Url</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {links.map(l => (
                        <tr key={l.id}>
                          <td>{l.title}</td>
                          <td>{l.description}</td>
                          <td>
                            <a href={l.url} title={l.url} target="_blank" rel="noreferrer" className="link-light text-decoration-underline">
                              link
                            </a>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={async () => {
                                try {
                                  await adminApi.DeleteBookingLink(l.id);
                                  setLinks(prev => prev.filter(x => x.id !== l.id));
                                } catch (e) {
                                  setError('Failed to delete booking link');
                                }
                              }}
                              aria-label="Delete booking link"
                              title="Delete"
                            >
                             Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBookingLink;


